import { useContext, useState } from "react";
import { depascalize, ListTableSort, mapToDropdownOption, SortOrder } from "sprint-common-web";

import { DistributorsDataContext } from "../../../common/data/lookup-data-providers/DistributorsDataProvider";
import { OrderRequestStatus } from "../../../common/types/OrderRequestStatus";

export interface OrderLookupFilters {
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: SortOrder;
    statuses: number[];
    content?: string;
    pONumber?: string;
    distributorId?: string;
    distributorName?: string;
    createdDateFrom?: Date;
    createdDateTo?: Date;
}

const statuses = [
    OrderRequestStatus.Pending,
    OrderRequestStatus.Invalid,
    OrderRequestStatus.CloudProcessing,
    OrderRequestStatus.OnPremProcessing,
    OrderRequestStatus.Cancelled
];

const defaultFilters: OrderLookupFilters = {
    page: 1,
    pageSize: 10,
    sortBy: "createdDate",
    sortOrder: "desc",
    statuses,
    content: "",
    pONumber: "",
    createdDateFrom: undefined,
    createdDateTo: undefined,
    distributorId: undefined,
    distributorName: undefined
};

export const useOrderLookupFilters = () => {
    const [filters, setFilters] = useState<OrderLookupFilters>(defaultFilters);

    const { suggestDistributor } = useContext(DistributorsDataContext);

    const setSort = (sort: ListTableSort) => {
        setFilters((prevFilters: OrderLookupFilters) => ({
            ...prevFilters,
            sortBy: sort.by,
            sortOrder: sort.order,
            page: 1
        }));
    };

    const setPage = (page: number) => {
        setFilters((prevFilters: OrderLookupFilters) => ({ ...prevFilters, page }));
    };

    const setStatuses = (orderStatuses: number[]): void => {
        setFilters((prevFilters: OrderLookupFilters) => ({ ...prevFilters, statuses: orderStatuses, page: 1 }));
    };

    const setContent = (content?: string): void => {
        setFilters((prevFilters: OrderLookupFilters) => ({ ...prevFilters, content, page: 1 }));
    };

    const setPONumber = (pONumber?: string): void => {
        setFilters((prevFilters: OrderLookupFilters) => ({ ...prevFilters, pONumber, page: 1 }));
    };

    const setDistributor = (distributorId?: string, distributorName?: string): void => {
        setFilters((prevFilters: OrderLookupFilters) => ({ ...prevFilters, distributorId, distributorName, page: 1 }));
    };

    const setCreatedDateFrom = (createdDateFrom: Date) => {
        setFilters((prevFilters: OrderLookupFilters) => ({ ...prevFilters, createdDateFrom }));
    };

    const setCreatedDateTo = (createdDateTo: Date) => {
        setFilters((prevFilters: OrderLookupFilters) => ({ ...prevFilters, createdDateTo }));
    };

    const clearFilters = (): void => {
        setFilters(defaultFilters);
    };

    const statusOptions = statuses.map((status) => ({ key: status, text: depascalize(OrderRequestStatus[status]) }));

    const filterDistributorOptions = async (query: string) => {
        const distributors = await suggestDistributor(query);

        return distributors.map((distributor) => mapToDropdownOption(distributor));
    };

    return {
        filters,
        statusOptions,
        setPage,
        setSort,
        setStatuses,
        setPONumber,
        setContent,
        setDistributor,
        setCreatedDateFrom,
        setCreatedDateTo,
        filterDistributorOptions,
        clearFilters
    };
};
